import * as React from "react"
import styled from "styled-components"
import { colors, scale } from "../../styles"
import { Flex } from "../Flex"
import { Icon } from "../Icon"
import { Text } from "../Text"
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form'

export interface CheckboxProps {
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => any
	disabled?: boolean
	id?: string
	name?: string
	value?: string
	checked?: boolean
	label?: React.ReactNode
	inputRef?: React.Ref<any>
	helperText?: string | FieldError | Merge<FieldError, FieldErrorsImpl<any>>
	error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>>
	defaultChecked?: boolean
}

interface LabelStyledProps {
	disabled?: boolean
}

const LabelStyled = styled.label<LabelStyledProps>`
	position: relative;
	display: inline-flex;
	cursor: pointer;
	font-size: ${scale.px(2)};
	line-height: ${scale.px(3)};
	color: ${colors.navy};
	user-select: none;
`

const LabelTextStyled = styled.span`
	padding-left: ${scale.px(1)};
	display: flex;
	align-items: center;
`

const CustomInputInner = styled.div<{ error: CheckboxProps["error"] }>`
	display: flex;
	align-items: center;

	// Checkbox square
	&:before {
		content: "";
		display: inline-block;
		width: ${scale.px(3)};
		height: ${scale.px(3)};
		border-radius: ${scale.px(0.5)};
		border: 1px solid ${({ error }) => (error ? colors.alert : colors.blue33)};
		background-color: ${colors.white};
		flex: 1 0 auto;
		align-self: flex-start;
	}
`

const CustomInputStyled = styled.div`
	position: relative;
	border: 1px solid transparent;
	border-radius: ${scale.px(0.5)};

	// Checkbox checkmark
	svg {
		position: absolute;
		top: -4px;
		left: -4px;
		transform: scale(0);
		transition: transform 0.1s ease;
	}

	&:hover {
		${CustomInputInner} {
			:before {
				border-color: ${colors.hoverBlue};
			}
		}
	}
`

const InputStyled = styled.input.attrs({ type: "checkbox" })`
	border: 0;
	outline: 0;
	height: 1px;
	margin: -1px;
	overflow: hidden;
	padding: 0;
	position: absolute;
	white-space: nowrap;
	width: 1px;

	&:checked + .checkbox {
		svg {
			transform: scale(1);
		}
	}

	&:focus + .checkbox {
		${CustomInputInner}:before {
			border-color: ${colors.hoverBlue};
		}
	}

	&:disabled + .checkbox {
		opacity: 0.5;
	}
`

const Checkbox: React.FC<CheckboxProps> = (props) => {
	const { helperText, error, label, id, disabled, inputRef, ...rest } = props

	return (
		<Flex flexDirection="column">
			<LabelStyled disabled={disabled} htmlFor={id}>
				<InputStyled ref={inputRef} id={id} disabled={disabled} {...rest} />
				<CustomInputStyled className="checkbox">
					<Icon icon="check" size={4} />
					<Flex flexDirection={"column"}>
						<CustomInputInner error={error}>
							<LabelTextStyled>{label}</LabelTextStyled>
						</CustomInputInner>
					</Flex>
				</CustomInputStyled>
			</LabelStyled>
			{helperText && (
				<Flex pl={4} w={1} minh={helperText ? "none" : 3}>
					<Text
						as="span"
						textColor={error ? "alert" : "navy"}
						fontSize={1.75}
						lineHeight={2.5}
						mt={0.5}
						mb={0}
					>
						<>
							{helperText}
						</>
					</Text>
				</Flex>
			)}
		</Flex>
	)
}

export { Checkbox }
